import React, { useState, useEffect } from "react";
import Layout from "./Layout";
import {
  createInitialForm,
  fetchLatestId,
  validateRequiredField,
} from "../../../common/common";
import { signUpData } from "../../../assets/lib/login";
import { COLLECTION, COLLECTION_COUNTER } from "../../../data/constant";
import { addDoc, collection, getDocs } from "firebase/firestore";
import { db } from "../../../config/firebase";
import { useNavigate } from "react-router-dom";
import { routes } from "../../../routing/routes";
import moment from "moment";

const TWO_FACTOR_API_KEY = "df62b83f-22ee-11ee-addf-0200cd936042";

function SignUp() {
  const [signUpState, setSignUpState] = useState(createInitialForm(signUpData));
  const [errorFields, setErrorFields] = useState([]);
  const [submitLoading, setSubmitLoading] = useState(false);

  const [phoneNoNot10Digit, setPhoneNoNot10Digit] = useState(false);
  const [alpinNot4Digit, setAlpinNot4Digit] = useState(false);
  const [existingUser, setExistingUser] = useState(false);
  const [isInvalidOtp, setIsInvalidOtp] = useState(false);

  const [emailOtp, setEmailOtp] = useState("");
  const [emailInvalidOtp, setEmailInvalidOtp] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [invalidCompanyEmail, setInvalidCompanyEmail] = useState(false);
  const [campusDisabled, setCampusDisabled] = useState(false); // ✅ NEW

  const [otp, setOtp] = useState("");
  const [userData, setUserData] = useState([]);

  const navigate = useNavigate();

  /* ---------- Fetch existing users ---------- */
  useEffect(() => {
    const fetchUserData = async () => {
      const snapshot = await getDocs(collection(db, COLLECTION.USER));
      const list = snapshot.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
      }));
      setUserData(list);
    };
    fetchUserData();
  }, []);

  /* ---------- Input ---------- */
  const getSignUpInput = (event) => {
    const { name, value, type } = event.target;

    let finalValue = value;
    if (type === "number" && value !== "") {
      finalValue = Number(value);
    }

    if (name === "email") {
      setInvalidCompanyEmail(false);
      setCampusDisabled(false);
      setEmailInvalidOtp(false);
      setEmailVerified(false);
    }

    if (errorFields.includes(name) && value) {
      setErrorFields((prev) => prev.filter((item) => item !== name));
    }

    setSignUpState((prev) => ({ ...prev, [name]: finalValue }));
  };

  /* ---------- Utils ---------- */
  const generateOTP = () =>
    Math.floor(1000 + Math.random() * 9000).toString();

  const isValidPhone = (num) => /^\d{10}$/.test(String(num));
  const isValidAlpin = (num) => /^\d{4}$/.test(String(num));

  /* ---------- Campus mapping (UPDATED) ---------- */
  const getCampusByEmail = async (email) => {
    const userDomain = email?.split("@")[1]?.toLowerCase();
    if (!userDomain) return null;

    const campusSnap = await getDocs(collection(db, COLLECTION.CAMPUS));

    for (const campusDoc of campusSnap.docs) {
      const admins = campusDoc.data().campusAdmins || [];

      const matchedAdmin = admins.find((admin) => {
        const adminDomain = admin.email?.split("@")[1]?.toLowerCase();
        return adminDomain === userDomain;
      });

      if (matchedAdmin) {
        return {
          campusId: campusDoc.data().campusId,
          campusName: campusDoc.data().campusName,
          disabled: matchedAdmin.disabled === true, // ✅ key logic
        };
      }
    }
    return null;
  };

  /* ---------- Email OTP (UPDATED) ---------- */
  const handleSendEmailOtp = async () => {
    setInvalidCompanyEmail(false);
    setCampusDisabled(false);

    if (!signUpState.email || !signUpState.userName) {
      setErrorFields(["userName", "email"]);
      return;
    }

    const campus = await getCampusByEmail(signUpState.email);

    // ❌ No campus found
    if (!campus) {
      setInvalidCompanyEmail(true);
      return;
    }

    // ❌ Campus admin disabled
    if (campus.disabled) {
      setCampusDisabled(true);
      return;
    }

    // ✅ Campus active
    setSignUpState((prev) => ({
      ...prev,
      campusId: campus.campusId,
      campusName: campus.campusName,
    }));

    const generatedOtp = generateOTP();
    setEmailOtp(generatedOtp);

    await fetch(
      "https://sendemailotp-713732302795.asia-south1.run.app",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: signUpState.email,
          otp: generatedOtp,
          userName: signUpState.userName,
        }),
      }
    );
  };

  const verifyEmailOtp = () => {
    if (String(signUpState.emailOtp) !== String(emailOtp)) {
      setEmailInvalidOtp(true);
      return;
    }
    setEmailVerified(true);
  };

  /* ---------- SMS OTP ---------- */
  const sendOTP = async (otp, phone) => {
    const response = await fetch(
      `https://2factor.in/API/V1/${TWO_FACTOR_API_KEY}/SMS/${phone}/${otp}/OTP1`
    );
    if (!response.ok) throw new Error("SMS OTP failed");
  };

  const handleSendSmsOtp = async () => {
    if (!emailVerified) return;

    let err = validateRequiredField(signUpData, signUpState);
    err = err.filter((x) => !["otp", "email", "emailOtp"].includes(x));
    setErrorFields(err);

    if (!isValidPhone(signUpState.phoneNumber))
      return setPhoneNoNot10Digit(true);

    if (!isValidAlpin(signUpState.newAlpin))
      return setAlpinNot4Digit(true);

    const exists = userData.some(
      (u) => Number(u.phoneNumber) === Number(signUpState.phoneNumber)
    );
    if (exists) return setExistingUser(true);

    const generatedOtp = generateOTP();
    setOtp(generatedOtp);

    await sendOTP(generatedOtp, Number(signUpState.phoneNumber));
  };

  /* ---------- Submit ---------- */
  const handleSubmitSignUp = async () => {
    if (String(signUpState.otp) !== String(otp)) {
      setIsInvalidOtp(true);
      return;
    }

    setSubmitLoading(true);

    try {
      const id = await fetchLatestId(COLLECTION_COUNTER.USER);

      await addDoc(collection(db, COLLECTION.USER), {
        userName: signUpState.userName,
        email: signUpState.email,
        phoneNumber: Number(signUpState.phoneNumber),
        alpin: String(signUpState.newAlpin),
        createdTimestamp: moment().unix(),
        hashedalpin: "",
        no: id,
        walletAmount: 0,
        goCoins: 0,
        status: "Active",
      });

      navigate(routes.USER_LOGIN);
    } catch (err) {
      console.error(err);
    }

    setSubmitLoading(false);
  };

  return (
    <Layout
      submitLoading={submitLoading}
      signUpState={signUpState}
      otp={otp}
      emailOtp={emailOtp}
      emailVerified={emailVerified}
      invalidCompanyEmail={invalidCompanyEmail}
      campusDisabled={campusDisabled} // ✅ PASS TO UI
      emailInvalidOtp={emailInvalidOtp}
      isInvalidOtp={isInvalidOtp}
      phoneNoNot10Digit={phoneNoNot10Digit}
      alpinNot4Digit={alpinNot4Digit}
      existingUser={existingUser}
      errorFields={errorFields}
      getSignUpInput={getSignUpInput}
      handleSendEmailOtp={handleSendEmailOtp}
      verifyEmailOtp={verifyEmailOtp}
      handleSendSmsOtp={handleSendSmsOtp}
      handleSubmitSignUp={handleSubmitSignUp}
    />
  );
}

export default SignUp;
